import { useQuery } from '@tanstack/react-query'
import type { UseQueryResult } from '@tanstack/react-query'
import { createContext, createElement, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { i18n } from '@/shared/i18n/i18n'
import { httpClient } from '@/shared/lib/httpClient'
import { apiV1 } from '@/shared/api/apiPrefix'
import { meQueryOptions } from '@/shared/api/meQueries'
import {
  brandAuthenticatedApiResponseSchema,
  brandPublicAppearanceResponseSchema,
  calendarColorsFromSnapshot,
  calendarMobileLayoutFromSnapshot,
  availabilityMaxSpanDaysFromSnapshot,
  homeLayoutFromSnapshot,
  isCalendarShowPrices,
  isCalendarTurnaroundEnabled,
  isGalleryLightboxEnabled,
  isSubsidyMentionVisible,
  isUnsubsidizedPriceVisible,
  mergeBrandAppearanceForShell,
  legalConsentFromSnapshot,
  partnerOfferSliderPositionFromSnapshot,
  requireCgvConsentFromSnapshot,
  type BrandAuthenticatedSnapshot,
  type BrandPublicAppearance,
  type BrandShellAppearance,
  type CalendarColorKey,
  type CalendarMobileLayoutPref,
  type HomeLayout,
  type LegalConsentPrefs,
  type PartnerOfferSliderPosition,
  type RequireCgvConsent,
} from '@/shared/schemas/brand.schema'

export const brandKeys = {
  all: ['brand'] as const,
  publicAppearance: () => [...brandKeys.all, 'public-appearance'] as const,
  authenticatedSnapshot: () => [...brandKeys.all, 'authenticated'] as const,
}

type BrandQueriesContextValue = {
  publicAppearance: UseQueryResult<BrandPublicAppearance, Error>
  portalSessionEstablished: UseQueryResult<boolean, Error>
  authenticatedSnapshot: UseQueryResult<BrandAuthenticatedSnapshot, Error>
  mergedShellAppearance: BrandShellAppearance | undefined
}

const BrandQueriesContext = createContext<BrandQueriesContextValue | null>(null)

function isAuthPathname(pathname: string): boolean {
  return pathname === '/auth' || pathname.startsWith('/auth/')
}

function isAnonymousPublicPathname(pathname: string): boolean {
  return (
    isAuthPathname(pathname) ||
    pathname.startsWith('/legal/') ||
    pathname.startsWith('/r/')
  )
}

/** Initial location only - login mutations seed the cache; avoids coupling to the router module. */
function shouldProbePortalSession(): boolean {
  if (typeof window === 'undefined') {
    return true
  }
  return !isAnonymousPublicPathname(window.location.pathname)
}

function usePortalSessionEstablishedQuery(enabled: boolean) {
  return useQuery({
    ...meQueryOptions,
    enabled,
    select: (): boolean => true,
  })
}

function useBrandPublicAppearanceQuery() {
  return useQuery({
    queryKey: brandKeys.publicAppearance(),
    queryFn: async (): Promise<BrandPublicAppearance> => {
      const res = await httpClient.get<unknown>(apiV1('/brand'))
      return brandPublicAppearanceResponseSchema.parse(res.data).data
    },
    staleTime: 5 * 60 * 1000,
  })
}

function useBrandAuthenticatedSnapshotQuery(sessionEstablished: boolean | undefined) {
  return useQuery({
    queryKey: brandKeys.authenticatedSnapshot(),
    queryFn: async (): Promise<BrandAuthenticatedSnapshot> => {
      const res = await httpClient.get<unknown>(apiV1('/brand'))
      return brandAuthenticatedApiResponseSchema.parse(res.data).data
    },
    enabled: sessionEstablished === true,
    staleTime: 5 * 60 * 1000,
  })
}

export function BrandQueriesProvider({ children }: { children: ReactNode }) {
  const publicAppearance = useBrandPublicAppearanceQuery()
  // Skip GET /me on /auth/* - session is expected absent; login mutations set the cache.
  const portalSessionEstablished = usePortalSessionEstablishedQuery(shouldProbePortalSession())
  const authenticatedSnapshot = useBrandAuthenticatedSnapshotQuery(portalSessionEstablished.data)
  const mergedShellAppearance = useMemo(
    () => mergeBrandAppearanceForShell(publicAppearance.data, authenticatedSnapshot.data),
    [publicAppearance.data, authenticatedSnapshot.data],
  )

  // `{{brandName}}` used by static i18n strings (error pages, editorial footer...) resolves
  // from the DB brand snapshot, never a hardcoded tenant name - see docs/MULTI-INSTANCE.md.
  const brandName = publicAppearance.data?.name
  useEffect(() => {
    if (!brandName) {
      return
    }
    const interpolation = i18n.options.interpolation ?? {}
    interpolation.defaultVariables = { ...interpolation.defaultVariables, brandName }
    i18n.options.interpolation = interpolation
    i18n.emit('languageChanged', i18n.language)
  }, [brandName])

  const value = useMemo<BrandQueriesContextValue>(
    () => ({
      publicAppearance,
      portalSessionEstablished,
      authenticatedSnapshot,
      mergedShellAppearance,
    }),
    [publicAppearance, portalSessionEstablished, authenticatedSnapshot, mergedShellAppearance],
  )

  return createElement(BrandQueriesContext.Provider, { value }, children)
}

function useBrandQueriesContext(): BrandQueriesContextValue {
  const context = useContext(BrandQueriesContext)
  if (context === null) {
    throw new Error('Brand query hooks must be used within BrandQueriesProvider.')
  }

  return context
}

export function usePortalSessionEstablished() {
  return useBrandQueriesContext().portalSessionEstablished
}

export function useBrandPublicAppearance() {
  return useBrandQueriesContext().publicAppearance
}

export function useBrandAuthenticatedSnapshot() {
  return useBrandQueriesContext().authenticatedSnapshot
}

export function useMergedBrandShellAppearance(): BrandShellAppearance | undefined {
  return useBrandQueriesContext().mergedShellAppearance
}

/** Galerie plein écran (lightbox) activable par l'admin via brand_settings. Défaut true. */
export function useGalleryLightboxEnabled(): boolean {
  return isGalleryLightboxEnabled(useBrandQueriesContext().authenticatedSnapshot.data)
}

/** Mise en page de l'accueil (hub dense ou cartes 3 colonnes) pilotée admin. Défaut 'hub'. */
export function useHomeLayout(): HomeLayout {
  return homeLayoutFromSnapshot(useBrandQueriesContext().authenticatedSnapshot.data)
}

/** T6.4 - position du slider offres partenaires à l'accueil, pilotée admin. Défaut 'off'. */
export function usePartnerOfferSliderPosition(): PartnerOfferSliderPosition {
  return partnerOfferSliderPositionFromSnapshot(useBrandQueriesContext().authenticatedSnapshot.data)
}

/** Consentement CGV/CGU (mode brand). Préférer `useLegalPackConsent` pour le statut API. */
export function useRequireCgvConsent(): RequireCgvConsent {
  return requireCgvConsentFromSnapshot(useBrandQueriesContext().authenticatedSnapshot.data)
}

/** Pack légal brand (`ui_preferences.legal_consent` + legacy require_cgv_consent). */
export function useLegalConsentPrefs(): LegalConsentPrefs {
  return legalConsentFromSnapshot(useBrandQueriesContext().authenticatedSnapshot.data)
}

/** Base / unsubsidized amount revealed by the admin toggle. Defaults to false. */
export function useUnsubsidizedPriceVisible(): boolean {
  return isUnsubsidizedPriceVisible(useBrandQueriesContext().authenticatedSnapshot.data)
}

/** “Après subvention” wording revealed by the admin toggle. Defaults to false. */
export function useSubsidyMentionVisible(): boolean {
  return isSubsidyMentionVisible(useBrandQueriesContext().authenticatedSnapshot.data)
}

/** CR-14: price under calendar days. Default true. */
export function useCalendarShowPrices(): boolean {
  return isCalendarShowPrices(useBrandQueriesContext().authenticatedSnapshot.data)
}

/** CR-14: mobile calendar layout preference. Default hybrid. */
export function useCalendarMobileLayout(): CalendarMobileLayoutPref {
  return calendarMobileLayoutFromSnapshot(useBrandQueriesContext().authenticatedSnapshot.data)
}

/** CR-14: turnaround / checkout-only. Property override wins when boolean. */
export function useCalendarTurnaroundEnabled(propertyOverride?: boolean | null): boolean {
  return isCalendarTurnaroundEnabled(
    useBrandQueriesContext().authenticatedSnapshot.data,
    propertyOverride,
  )
}

/** CR-14: optional calendar status color overrides from brand. */
export function useCalendarColors(): Partial<Record<CalendarColorKey, string>> {
  return calendarColorsFromSnapshot(useBrandQueriesContext().authenticatedSnapshot.data)
}

/** CR-14: max availability API window in days (admin reservation global settings). Default 365. */
export function useAvailabilityMaxSpanDays(): number {
  return availabilityMaxSpanDaysFromSnapshot(useBrandQueriesContext().authenticatedSnapshot.data)
}
